import { useCallback, useEffect, useRef, useState } from 'react';

import { HumanProof, SignupConfig, useSignupApi } from '../../common/hooks/use-signup-api.hook';
import { solveChallenge } from './pow';

export type HumanCheckMode = 'loading' | 'pow' | 'turnstile';
export type HumanCheckState = 'idle' | 'working' | 'ready' | 'failed';

/** A solved challenge is renewed this long before it expires. */
const EXPIRY_MARGIN_MS = 15_000;

/**
 * The server refuses a challenge spent sooner than `minFillMs` after it was
 * issued (2.5 s). It matters on a retry: after a refusal the next challenge
 * arrives at once, and someone who fixes a typo quickly would be told they are
 * a bot. So a proof is held until it is old enough, counted from when this
 * page received it — always after the server issued it, so no clock needs to
 * agree — plus a margin.
 */
export const DEFAULT_MIN_FILL_MS = 2_500;
const MIN_FILL_MARGIN_MS = 300;

/**
 * The human check behind the signup form, whichever kind the server wants.
 *
 * Turnstile when the API has a site key (the widget hands a token back);
 * otherwise a proof-of-work puzzle, fetched and solved in a worker the moment
 * the page opens, so it is usually done before the person has typed their
 * email. Each proof is single-use: after any attempt, `renew()` gets another.
 *
 * `waitForProof()` is what the submit awaits — instant when the proof is
 * ready, otherwise it waits for it. A check that could not run resolves with
 * an empty proof and lets the server decide (it answers HUMAN_CHECK_FAILED).
 */
export function useHumanCheck(config: SignupConfig | null | undefined) {
    const api = useSignupApi();
    const mode: HumanCheckMode = config === undefined ? 'loading' : config?.turnstileSiteKey ? 'turnstile' : 'pow';

    const [state, setState] = useState<HumanCheckState>('idle');
    const proof = useRef<HumanProof | null>(null);
    const receivedAt = useRef(0);
    const waiters = useRef<((p: HumanProof) => void)[]>([]);
    const abort = useRef<AbortController | null>(null);
    const expiry = useRef<number | undefined>(undefined);
    const resetTurnstile = useRef<(() => void) | null>(null);

    const settle = useCallback((p: HumanProof | null) => {
        proof.current = p;
        setState(p ? 'ready' : 'failed');
        const pending = waiters.current.splice(0);
        pending.forEach((resolve) => resolve(p ?? {}));
    }, []);

    const solve = useCallback(async () => {
        abort.current?.abort();
        window.clearTimeout(expiry.current);
        const controller = new AbortController();
        abort.current = controller;
        proof.current = null;
        setState('working');
        try {
            const challenge = await api.challenge();
            receivedAt.current = Date.now();
            const { nonce } = await solveChallenge(challenge, controller.signal);
            if (controller.signal.aborted) return;
            settle({ challenge: { ...challenge, nonce } });
            const expiresAt = new Date(challenge.expiresAt as any).getTime();
            if (Number.isFinite(expiresAt)) {
                expiry.current = window.setTimeout(() => solve(), Math.max(1_000, expiresAt - Date.now() - EXPIRY_MARGIN_MS));
            }
        } catch {
            if (!controller.signal.aborted) settle(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settle]);

    // The puzzle starts as soon as we know it is the puzzle.
    useEffect(() => {
        if (mode === 'pow') solve();
        return () => {
            abort.current?.abort();
            window.clearTimeout(expiry.current);
        };
    }, [mode, solve]);

    /** The Turnstile widget reports here: a token, or null when it expired or failed. */
    const onTurnstileToken = useCallback(
        (token: string | null) => {
            if (token) settle({ turnstileToken: token });
            else {
                proof.current = null;
                setState('working');
            }
        },
        [settle],
    );

    const onTurnstileError = useCallback(() => settle(null), [settle]);

    /** A fresh proof after an attempt used this one. */
    const renew = useCallback(() => {
        proof.current = null;
        if (mode === 'pow') solve();
        else if (mode === 'turnstile') {
            setState('working');
            resetTurnstile.current?.();
        }
    }, [mode, solve]);

    const waitForProof = useCallback(
        (timeoutMs = 30_000): Promise<HumanProof> => {
            // A puzzle proof younger than the server's minimum waits out the rest.
            const aged = (p: HumanProof): Promise<HumanProof> => {
                const minFill = config?.minFillMs ?? DEFAULT_MIN_FILL_MS;
                const wait = p.challenge && minFill > 0 ? minFill + MIN_FILL_MARGIN_MS - (Date.now() - receivedAt.current) : 0;
                return wait > 0 ? new Promise((resolve) => window.setTimeout(() => resolve(p), wait)) : Promise.resolve(p);
            };
            if (proof.current) return aged(proof.current);
            if (state === 'failed') return Promise.resolve({});
            return new Promise<HumanProof>((resolve) => {
                const id = window.setTimeout(() => resolve({}), timeoutMs);
                waiters.current.push((p) => {
                    window.clearTimeout(id);
                    resolve(p);
                });
            }).then(aged);
        },
        [state, config?.minFillMs],
    );

    return { mode, state, waitForProof, renew, onTurnstileToken, onTurnstileError, resetTurnstile };
}
